import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import style from "../common.module.scss"
import CloseIcon from '../../../shared/icons/CloseIcon';
import EditIconNew from '../../../shared/icons/EditIconNew';
import PlusIcon from '../../../shared/icons/PlusIcon';
import MoreIcon from '../../../shared/icons/MoreIcon';
import { ORDER_STATUSES_DATA } from '../../../../mock';
import BallIcon from '../../../shared/icons/BallIcon';
import CancelApp from '../../../order/info/CancelApp';
import { File, Order, User } from '../../../../services';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useSelector } from "react-redux";
import OrdersDelete from '../../../order/info/OrdersDelete';
import EyeIcon from '../../../shared/icons/EyeIcon';
import OrderViewsModal from './OrderViewsModal';
import CopyIconNew from '../../../shared/icons/CopyIconNew';
import { useScreenSize } from '../../../../hooks/useScreenSize';
import { AlarmClockPlusIcon, FileDigitIcon, FileDownIcon, Share2Icon, ShareIcon, Trash2Icon, Undo2Icon } from 'lucide-react';
import { useClickOutside } from '../../../../hooks/useClickOutside';
import ConfirmModal from '../../../shared/ConfirmModal/ConfirmModal';
import SendToMarketplaceModal from './SendToMarketplaceModal';
import ExtendTenderEndDate from './ExtendTenderEndDate/ExtendTenderEndDate';
import AppTooltip from '../../../shared/AppTooltip/AppTooltip';
import AddProjectNumberModal from './AddProjectNumberModal/AddProjectNumberModal';
import { getRailwayOrderPath, isRailwayOrder } from "../../../../pages/orders/utils/railwayOrder";

const OrdersInfoHeader = ({ item, setIsOpen, onForceUpdate, orderViews = [], isShowMore = true }) => {
    const { t, i18n } = useTranslation();
    const { isTabletSmallView } = useScreenSize();
    const { user } = useSelector((state) => state);
    const navigate = useNavigate()
    const moreRef = useRef(null);
    const copyLinkRef = useRef(null);

    const [isOpenDropdowns, setIsOpenDropdowns] = useState(false);
    const [isCancel, setCancelModal] = useState(false);
    const [isDelete, setDelete] = useState(false);
    const [isViewersModalOpen, setIsViewersModalOpen] = useState(false);
    const totalQuantity = orderViews.reduce((sum, item) => sum + item.quantity, 0);

    const [shareLink, setShareLink] = useState("");
    const [isShareDropdownOpen, setIsShareDropdownOpen] = useState(false);
    const [showConfirmMarketplaceModal, setShowConfirmMarketplaceModal] = useState(false);
    const [showExtendTenderModal, setShowExtendTenderModal] = useState(false);
    const [showAddProjectNumberModal, setShowAddProjectNumberModal] = useState(false);

    const editPath = isRailwayOrder(item) && user?.type === "Client"
      ? getRailwayOrderPath("edit", item.id)
      : item?.isComplexTender && user?.type === "Client"
      ? `/orders/complex-tender/edit/${item?.id}`
      : user?.type === "Client"
        ? `/orders/edit/${item?.id}`
        : `/expeditor/orders/edit/${item?.id}`;
    const copyPath = isRailwayOrder(item) && user?.type === "Client"
      ? getRailwayOrderPath("copy", item.id)
      : item?.isComplexTender && user?.type === "Client"
      ? `/orders/complex-tender/copy/${item?.id}`
      : user?.type === "Client"
        ? `/orders/copy/${item?.id}`
        : `/expeditor/orders/copy/${item?.id}`;

    const shouldShowButton = () => {
      return (
        item?.status === "published" ||
        item?.status === "awaitingoffer" ||
        item?.status === "assigned" ||
        item?.status === "moderation" ||
        item?.status === "awaiting_payment" ||
        item?.status === "rejected" ||
        item?.status === "canceled"
      );
    };

    const shouldShowWithdrawButton = () => {
      return (
        item?.status === "published" ||
        item?.status === "awaitingoffer" ||
        item?.status === "assigned" ||
        item?.status === "assignedexecutor" ||
        item?.status === "booked" ||
        item?.status === "confirmed" ||
        item?.status === "moderation" ||
        item?.status === "awaiting_payment" ||
        item?.status === "rejected"
      );
    };

    const handleDownloadClick = () => {
        setIsOpenDropdowns(false)
    };

    const handleClickOutside = (event) => {
        if (moreRef.current && !moreRef.current.contains(event.target)) {
            setIsOpenDropdowns(false);
        }
    };

    const downloadInvoice = async (orderId) => {
        toast.success(t('orderInfo.ordersInfoHeader.copySuccess')); // Assuming this is a generic success message
        try {
          const response = await File.getDocPayment(orderId);
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `${item.number}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.parentNode.removeChild(link);
        } catch (error) {
          console.error('Ошибка загрузки платежного документа:', error);
        }
    };

    const downloadOrderPdf = async () => {
        try {
            const response = await File.getOrderPdf(item.id, i18n.language);
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `order_${item.number}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Ошибка выгрузки PDF заказа:', error);
            toast.error('Не удалось выгрузить PDF заказа');
        }
        setIsOpenDropdowns(false);
    };

    const handleViewsClick = () => {
        setIsViewersModalOpen(true);
    };

    const handleShareClick = async () => {
        if (shareLink) {
        setIsShareDropdownOpen(!isShareDropdownOpen);
        } else {
        try {
            const response = await User.guestLogin(item.id);
            const accessToken = response.data.accessToken;
            const link = `${window.location.origin}/order/share/${accessToken}`;
            setShareLink(link);
            setIsShareDropdownOpen(true);
        } catch (error) {
            console.error("Error generating share link:", error);
            toast.error(t("orderInfo.genericError"));
        }
        }
    };

    const handleCopyLink = () => {
        navigator.clipboard.writeText(shareLink);
        toast.success(t("orderInfo.ordersInfoHeader.linkCopied"));
        setIsShareDropdownOpen(false);
    };

    const handleMarketplaceSuccess = () => {
        setShowConfirmMarketplaceModal(false);
        if (onForceUpdate) {
            onForceUpdate();
        }
    };

    useClickOutside(copyLinkRef, () => setIsShareDropdownOpen(false));

    useEffect(() => {
        if (isOpenDropdowns) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpenDropdowns]);

    useEffect(() => {
        setShareLink("")
    }, [item]);

    return (
        <div className={cn(style.header_wrapper)}>
            <CloseIcon
                fill="#6D7885"
                width="24"
                height="24"
                onClick={() => {
                if (setIsOpen) {
                    setIsOpen(false);
                } else {
                    navigate(-1);
                }
                }}
                style={{ cursor: "pointer" }}
            />
            <div className={style.header_text}>
                <div className={style.orders_title}>{item?.cityFromName} - {item?.cityToName}</div>
                <div className={style.orders_status} style={{ color: ORDER_STATUSES_DATA(t)[item?.status]?.color }}>
                    <BallIcon fill={ORDER_STATUSES_DATA(t)[item?.status]?.color} />
                    {ORDER_STATUSES_DATA(t)[item?.status]?.title || item?.status}
                </div>
            </div>
            {user?.companyId === item?.companyId &&(
                <div className={style.views} onClick={handleViewsClick} style={{ cursor: 'pointer' }}>
                    <EyeIcon />
                    <div className={style.views_count}>{totalQuantity}</div>
                </div>
            )}
            {shouldShowButton() && user?.companyId === item?.companyId &&(
                <>
                  <EditIconNew
                    style={{ cursor: 'pointer' }}
                    data-tooltip-id="orders-info-edit-tooltip"
                    onClick={() => navigate(editPath)}
                  />
                  <AppTooltip id="orders-info-edit-tooltip" place="bottom" positionStrategy="fixed">
                    {t('tooltips.editOrder')}
                  </AppTooltip>
                </>
            )}
            {user?.companyId === item?.companyId &&
                (isTabletSmallView ? (
                <CopyIconNew title="copy"
                    onClick={() => navigate(copyPath)}
                />
                ) : (
                <button
                    className={style.header_btn}
                    onClick={() => navigate(copyPath)}
                >
                    <PlusIcon /> {t("orderInfo.ordersInfoHeader.copyButton")}
                </button>
                ))}
            {isShowMore && (
                <div className={style.header_more} ref={moreRef}>
                <MoreIcon
                    data-tooltip-id="orders-info-more-tooltip"
                    onClick={() => {
                    setIsOpenDropdowns(!isOpenDropdowns);
                    }}
                />
                <AppTooltip id="orders-info-more-tooltip" place="bottom" positionStrategy="fixed">
                  {t('tooltips.additionalMenu')}
                </AppTooltip>
                <div className={cn(style.header_dropdowns, { [style.header_dropdowns_open]: isOpenDropdowns })}>
                    {/* <div className={style.header_dropdowns_item} onClick={handleDownloadClick}>Отправить в черновик</div> */}
                    {/* <div className={cn(style.header_dropdowns_item)} onClick={() => downloadInvoice(item.id)}>{t('orderInfo.ordersInfoHeader.downloadInvoiceButton')}</div> */}

                    {user?.companyId === item?.companyId && item?.status === "awaitingoffer" && (
                        <div>
                            <div
                            className={style.header_dropdowns_item}
                            onClick={() => {
                                setShowExtendTenderModal(true);
                                setIsOpenDropdowns(false);
                            }}
                            style={{ cursor: "pointer" }}
                            >
                            <AlarmClockPlusIcon size={18} color="#6D7885" />
                                {t("orderInfo.ordersInfoHeader.extendTenderAction")}
                            </div>
                        </div>
                    )}

                    {user?.companyId === item?.companyId && (
                        <div
                            className={style.header_dropdowns_item}
                            onClick={downloadOrderPdf}
                            style={{ cursor: "pointer" }}
                        >
                            <FileDownIcon size={18} color="#6D7885" />
                            {t("orderInfo.ordersInfoHeader.generateOrderPdf")}
                        </div>
                    )}
                    {user?.companyId === item?.companyId && (
                        <div
                            className={style.header_dropdowns_item}
                            onClick={() => {
                                setShowAddProjectNumberModal(true);
                                setIsOpenDropdowns(false);
                            }}
                            style={{ cursor: "pointer" }}
                        >
                            <FileDigitIcon size={18} color="#6D7885" />
                            {t("orderInfo.ordersInfoHeader.addProjectNumber")}
                        </div>
                    )}

                    {item?.status !== "completed" && (
                        <div ref={copyLinkRef}>
                            <div
                            className={style.header_dropdowns_item}
                            onClick={handleShareClick}
                            style={{ cursor: "pointer" }}
                            >
                        <Share2Icon size={18} color="#6D7885" />
                            {t("orderInfo.ordersInfoHeader.shareOrder")}
                            </div>

                            {shareLink && isShareDropdownOpen && (
                            <div className={style.share_dropdown_modern}>
                                <div className={style.share_link_box}>
                                <input type="text" value={shareLink} readOnly className={style.share_input_modern} />
                                <button className={style.copy_btn_modern} onClick={handleCopyLink}>
                                    <CopyIconNew />
                                    <span>{t("orderInfo.ordersInfoHeader.copy")}</span>
                                </button>
                                </div>
                            </div>
                            )}
                        </div>
                    )}

                    {shouldShowWithdrawButton() && user?.companyId === item.companyId && (
                        <div
                            className={cn(style.header_dropdowns_item, style.header_dropdowns_reset)}
                            onClick={() => setCancelModal(true)}
                        >
                            <Undo2Icon size={18} color="#E53935" />
                            {t("orderInfo.ordersInfoHeader.withdrawOrderButton")}
                        </div>
                    )}
                    {shouldShowButton() && user?.companyId === item.companyId && (
                        <div
                            className={cn(style.header_dropdowns_item, style.header_dropdowns_reset)}
                            onClick={() => setDelete(true)}
                        >
                            <Trash2Icon size={18} color="#E53935" />
                            {t("orderInfo.ordersInfoHeader.deleteOrderButton")}
                        </div>
                    )}
                </div>
                </div>
            )}
            {isCancel &&
                <CancelApp
                    item={item}
                    onClose={() => setCancelModal(false)}
                    onUpdateData={() => onForceUpdate()}
                />
            }
            {isDelete &&
                <OrdersDelete
                    item={item}
                    onClose={() => setDelete(false)}
                    onUpdateData={() => onForceUpdate()}
                    setIsOpen={setIsOpen}
                />
            }
            
            <OrderViewsModal
                isOpen={isViewersModalOpen}
                onClose={() => setIsViewersModalOpen(false)}
                orderViews={orderViews}
            />

            <SendToMarketplaceModal
                isOpen={showConfirmMarketplaceModal}
                orderId={item ? item.id : ""}
                tenderDateEnd={item?.tenderDateEnd}
                onSuccess={handleMarketplaceSuccess}
                onCancel={() => setShowConfirmMarketplaceModal(false)}
            />

            <ExtendTenderEndDate
                isOpen={showExtendTenderModal}
                orderId={item ? item.id : ""}
                tenderDateEnd={item?.tenderDateEnd}
                assignmentMethod={item?.assignmentMethod}
                onSuccess={() => {
                    setShowExtendTenderModal(false);
                    if (onForceUpdate) {
                        onForceUpdate();
                    }
                }}
                onCancel={() => setShowExtendTenderModal(false)}
            />

            <AddProjectNumberModal
                isOpen={showAddProjectNumberModal}
                orderId={item?.id}
                onSuccess={() => {
                    setShowAddProjectNumberModal(false);
                    if (onForceUpdate) {
                        onForceUpdate();
                    }
                }}
                onCancel={() => setShowAddProjectNumberModal(false)}
            />
        </div>
    );
};

export default OrdersInfoHeader;
